import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { config } from '@/api/endpoints';
import { Notice } from '@/components/Notice';
import { RequirePermission } from '@/components/RequirePermission';
import { describe } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { useAuth } from '@/stores/auth';

/**
 * Limits where a larger number means more risk.
 *
 * Used only to warn before submitting. The server classifies authoritatively
 * and refuses what the caller may not do, including keys this list does not
 * know about, so a stale copy here cannot widen a limit by accident.
 */
const UPWARD_IS_RISKIER = new Set([
  'max_order_notional_quote',
  'max_position_notional_quote',
  'max_exposure_per_asset_quote',
  'max_total_exposure_quote',
  'max_concurrent_positions',
  'max_open_orders',
  'max_trades_per_hour',
  'daily_loss_limit_quote',
  'max_drawdown_pct',
  'max_holding_days',
  'max_slippage_bps',
  'max_spread_bps',
  'global_max_exposure_quote',
  'global_daily_loss_limit_quote',
]);
const DOWNWARD_IS_RISKIER = new Set([
  'min_24h_volume_quote',
  'min_depth_1pct_quote',
  'max_market_data_staleness_seconds',
]);

function looksLikeRelaxation(key: string, before: unknown, after: string): boolean {
  const previous = Number(before);
  const next = Number(after);
  if (!Number.isFinite(previous) || !Number.isFinite(next) || previous === next) return false;
  if (UPWARD_IS_RISKIER.has(key)) return next > previous;
  if (DOWNWARD_IS_RISKIER.has(key)) return next < previous;
  return true;
}

export function RiskPage() {
  const queryClient = useQueryClient();
  const can = useAuth((s) => s.can);
  const [selected, setSelected] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [changeNote, setChangeNote] = useState('');
  const [message, setMessage] = useState<{ tone: 'info' | 'error' | 'warn'; text: string } | null>(null);

  const profiles = useQuery({ queryKey: ['risk-profiles'], queryFn: config.riskProfiles });
  const activeProfile = useMemo(
    () => (profiles.data ?? []).find((profile) => profile.id === selected) ?? profiles.data?.[0],
    [profiles.data, selected],
  );
  const versions = useQuery({
    queryKey: ['risk-versions', activeProfile?.id],
    queryFn: () => config.riskVersions(activeProfile!.id),
    enabled: Boolean(activeProfile),
  });

  const save = useMutation({
    mutationFn: () => config.updateRiskLimits(activeProfile!.id, edits, changeNote),
    onSuccess: (result) => {
      setEdits({});
      setChangeNote('');
      setMessage({
        tone: result.relaxed_keys.length ? 'warn' : 'info',
        text: result.relaxed_keys.length
          ? `Saved as version ${result.version}. Loosened: ${result.relaxed_keys.join(', ')}.`
          : `Saved as version ${result.version}.`,
      });
      void queryClient.invalidateQueries({ queryKey: ['risk-profiles'] });
      void queryClient.invalidateQueries({ queryKey: ['risk-versions'] });
    },
    onError: (error) => setMessage({ tone: 'error', text: describe(error) }),
  });

  const pendingRelaxations = Object.entries(edits).filter(([key, value]) =>
    looksLikeRelaxation(key, activeProfile?.limits?.[key], value),
  );

  return (
    <>
      <div className="page-head">
        <h1>Risk limits</h1>
        <p>
          Limits are versioned, never overwritten. Every order records the version it was approved
          under. Tightening needs an admin; loosening needs a super admin who re-enters their
          password.
        </p>
      </div>

      {message && <Notice tone={message.tone}>{message.text}</Notice>}

      {(profiles.data ?? []).length === 0 ? (
        <section className="panel">
          <div className="empty">
            No risk profile yet. Run <span className="num">seed-defaults</span> to create one with
            the shipped conservative limits.
          </div>
        </section>
      ) : (
        <>
          {(profiles.data ?? []).length > 1 && (
            <div className="toolbar">
              <label htmlFor="profile">Profile</label>
              <select
                id="profile"
                value={activeProfile?.id ?? ''}
                onChange={(event) => setSelected(Number(event.target.value))}
                style={{ maxWidth: 260 }}
              >
                {(profiles.data ?? []).map((profile) => (
                  <option key={profile.id} value={profile.id}>
                    {profile.name} ({profile.scope.toLowerCase()})
                  </option>
                ))}
              </select>
            </div>
          )}

          <section className="panel">
            <div className="panel-head">
              <h2>{activeProfile?.name}</h2>
              <span className="hint">version {versions.data?.[0]?.version ?? '—'}</span>
            </div>
            <div className="panel-body flush">
              <table>
                <thead>
                  <tr>
                    <th>Limit</th>
                    <th className="right">Current</th>
                    <th className="right">New value</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(activeProfile?.limits ?? {}).map(([key, value]) => {
                    const edited = edits[key];
                    const relaxing = edited !== undefined && looksLikeRelaxation(key, value, edited);
                    return (
                      <tr key={key}>
                        <td>{key.replace(/_/g, ' ')}</td>
                        <td className="right num">{String(value)}</td>
                        <td className="right">
                          <RequirePermission
                            permission="config:risk_tighten"
                            fallback={<span className="hint">read only</span>}
                          >
                            <input
                              className="num"
                              aria-label={`New value for ${key}`}
                              style={{
                                textAlign: 'right',
                                maxWidth: 160,
                                borderColor: relaxing ? 'var(--warn)' : undefined,
                              }}
                              value={edited ?? ''}
                              placeholder="unchanged"
                              onChange={(event) =>
                                setEdits((current) => {
                                  const next = { ...current };
                                  if (event.target.value === '') delete next[key];
                                  else next[key] = event.target.value;
                                  return next;
                                })
                              }
                            />
                          </RequirePermission>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {Object.keys(edits).length > 0 && (
              <div className="panel-body" style={{ borderTop: '1px solid var(--line)' }}>
                {pendingRelaxations.length > 0 && (
                  <Notice tone="warn">
                    This loosens {pendingRelaxations.map(([key]) => key).join(', ')}.{' '}
                    {can('config:risk_relax')
                      ? 'You will be asked to confirm your password.'
                      : 'Your role cannot loosen limits; the server will refuse this.'}
                  </Notice>
                )}
                <label htmlFor="change-note">Why are you changing this?</label>
                <input
                  id="change-note"
                  value={changeNote}
                  onChange={(event) => setChangeNote(event.target.value)}
                  placeholder="Recorded in the audit trail"
                />
                <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
                  <button type="button" onClick={() => setEdits({})}>
                    Discard
                  </button>
                  <button
                    type="button"
                    className="primary"
                    disabled={save.isPending || !changeNote}
                    onClick={() => save.mutate()}
                  >
                    {save.isPending ? 'Saving…' : 'Save new version'}
                  </button>
                </div>
              </div>
            )}
          </section>

          <section className="panel">
            <div className="panel-head">
              <h2>History</h2>
            </div>
            <div className="panel-body flush">
              <table>
                <thead>
                  <tr>
                    <th className="right">Version</th>
                    <th>When</th>
                    <th>Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(versions.data ?? []).map((version) => (
                    <tr key={version.id}>
                      <td className="right num">{version.version}</td>
                      <td className="num">{formatDateTime(version.created_at)}</td>
                      <td style={{ color: 'var(--text-secondary)' }}>{version.change_note ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}
    </>
  );
}
